import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Coins, Loader2 } from 'lucide-react';

import { updateAction } from '../../../redux/DigestSlice/digestSlice';
import { CONTACTS_PER_FETCH_OPTIONS, creditsFor } from './digestHelpers';

/**
 * The per-row control on a fetch_contact action: pick 10/20/30/40/50 contacts,
 * PATCH contactsRequested, and show the server-recomputed creditCost the
 * instant it comes back. Shared by the grid card and the detail modal so the
 * two surfaces can never show a different price for the same row.
 */
const ContactCountPicker = ({ digestId, action, disabled }) => {
    const dispatch = useDispatch();
    const [saving, setSaving] = useState(false);
    const contactsRequested = action.contactsRequested || CONTACTS_PER_FETCH_OPTIONS[0];
    // Prefer the server's own creditCost (authoritative); fall back to the
    // client-side maths only while a change is in flight and hasn't round-tripped.
    const creditCost = action.creditCost ?? creditsFor(contactsRequested);

    const choose = async (n) => {
        if (n === contactsRequested || saving || disabled) return;
        setSaving(true);
        await dispatch(updateAction({ digestId, actionId: action.actionId, contactsRequested: n }));
        setSaving(false);
    };

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <div className="inline-grid grid-cols-5 gap-0.5 p-0.5 rounded-xl bg-slate-100 w-full">
                {CONTACTS_PER_FETCH_OPTIONS.map((n) => {
                    const active = n === contactsRequested;
                    return (
                        <button
                            key={n}
                            type="button"
                            disabled={disabled || saving}
                            onClick={() => choose(n)}
                            className={`py-1.5 rounded-[9px] text-[12.5px] font-bold transition-all disabled:opacity-50
                                ${active
                                    ? 'bg-[#3F57E0] text-white shadow-sm'
                                    : 'text-slate-500 hover:text-slate-800 hover:bg-white/70'}`}
                        >
                            {n}
                        </button>
                    );
                })}
            </div>
            <p className="mt-2 text-[12.5px] text-slate-500 flex items-center gap-1.5">
                {saving ? (
                    <Loader2 size={12} className="animate-spin shrink-0" />
                ) : (
                    <Coins size={12} className="shrink-0" />
                )}
                <span><span className="font-semibold text-slate-700">{contactsRequested}</span> contacts · <span className="font-semibold text-[#3F57E0]">{creditCost}</span> credit{creditCost === 1 ? '' : 's'}</span>
            </p>
        </div>
    );
};

export default ContactCountPicker;
